import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { GoogleOAuthProvider } from "@react-oauth/google";
import { HelmetProvider } from "react-helmet-async";
import { Toaster } from "sonner";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ScrollToTop from "@/components/ScrollToTop";
import HomePage from "@/pages/HomePage";
import LanguageSelectPage from "@/pages/LanguageSelectPage";
import SetsListPage from "@/pages/SetsListPage";
import SetDetailPage from "@/pages/SetDetailPage";
import CardDetailPage from "@/pages/CardDetailPage";
import ShowcasePage from "@/pages/ShowcasePage";
import SearchResultsPage from "@/pages/SearchResultsPage";
import ProfilePage from "@/pages/ProfilePage";
import LeaderboardPage from "@/pages/LeaderboardPage";
import AdminPage from "@/pages/AdminPage";
import AdminModerationPage from "@/pages/AdminModerationPage";
import AdminMappingsPage from "@/pages/AdminMappingsPage";
import AdminCatalogPage from "@/pages/AdminCatalogPage";
import AboutPage from "@/pages/AboutPage";
import PrivacyPage from "@/pages/PrivacyPage";
import TermsPage from "@/pages/TermsPage";
import { LegacyCardRedirect, LegacySetRedirect } from "@/pages/LegacyRedirects";

/*
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID;

function NotFound() {
  return (
    <div
      className="mx-auto w-full max-w-[1400px] px-5 py-24 md:px-gutter"
      data-testid="not-found-page"
    >
      <div className="text-eyebrow uppercase text-ink-muted">404</div>
      <h1 className="mt-1 text-title-page text-ink-text">Nothing here</h1>
      <p className="mt-2 text-body text-ink-muted">
        That page isn't part of the catalog yet.
      </p>
    </div>
  );
}

/**
 * Route table — owner directive 2026-02-01 URL scheme.
 *
 * Canonical shape (game-at-root):
 *   /                                                        Home / TCG picker
 *   /{game}                                                  Language select
 *   /{game}/{lang}/sets                                      Sets list
 *   /{game}/{lang}/sets/{set-slug}                           Cards in set
 *   /{game}/{lang}/sets/{set-slug}/{card-slug}/{n}[/{v}]     Card page
 *
 * Static routes register alongside the `:game`-parametric routes;
 * React Router v6 ranks static segments higher than dynamic ones so
 * /search / /profile / /leaderboard / /admin / /users / /about / /privacy
 * / /terms always win over /:game. The `:game` param is validated
 * inside `LanguageSelectPage`.
 *
 * SEO pass (2026-02-01) additions:
 *   - `HelmetProvider` wraps the app so every page contributes per-page
 *     head tags for client-side navigation. Initial document render
 *     is backend meta-injected (dev-server middleware or hosting rewrite).
 *   - `<Footer />` renders on every page.
 *   - New public static pages: /about, /privacy, /terms.
 */
function AppRoutes() {
  return (
    <div className="App flex min-h-screen flex-col bg-ink-bg text-ink-text">
      <BrowserRouter>
        <ScrollToTop />
        <Header />
        <main className="flex-1">
          <Routes>
            <Route path="/" element={<HomePage />} />

            {/* Static app routes — must beat the /:game catch-all. */}
            <Route path="/search" element={<SearchResultsPage />} />
            <Route path="/profile" element={<ProfilePage ownView />} />
            <Route path="/users/:userId" element={<ProfilePage ownView={false} />} />
            <Route path="/leaderboard" element={<LeaderboardPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/admin" element={<AdminPage />} />
            <Route path="/admin/moderation" element={<AdminModerationPage />} />
            <Route path="/admin/mappings" element={<AdminMappingsPage />} />
            <Route path="/admin/catalog" element={<AdminCatalogPage />} />

            {/* Legacy redirects (pre-remap URLs — kept working). */}
            <Route path="/sets" element={<Navigate to="/pokemon/en/sets" replace />} />
            <Route path="/sets/:externalId" element={<LegacySetRedirect />} />
            <Route path="/cards/:externalId" element={<LegacyCardRedirect />} />
            <Route
              path="/cards/:externalId/:variantSlug"
              element={<LegacyCardRedirect />}
            />

            {/* Canonical game-parametric routes. */}
            <Route path="/:game" element={<LanguageSelectPage />} />
            <Route path="/:game/:lang/sets" element={<SetsListPage />} />
            <Route path="/:game/:lang/sets/:setSlug" element={<SetDetailPage />} />
            {/*
             * Showcase pages — nested child of card+variant. Static
             * "showcase" segment beats the /:number/* splat card page
             * in React Router v6's specificity ranking. Two shapes:
             *   /:game/:lang/sets/:setSlug/:cardSlug/:number/showcase/:submissionId
             *     bare-canonical (parent card is the DEFAULT variant)
             *   /:game/:lang/sets/:setSlug/:cardSlug/:number/:variantSlug/showcase/:submissionId
             *     non-default variant
             * `showcase` is reserved — see /app/frontend/src/lib/slug.js.
             */}
            <Route
              path="/:game/:lang/sets/:setSlug/:cardSlug/:number/showcase/:submissionId"
              element={<ShowcasePage />}
            />
            <Route
              path="/:game/:lang/sets/:setSlug/:cardSlug/:number/:variantSlug/showcase/:submissionId"
              element={<ShowcasePage />}
            />
            <Route
              path="/:game/:lang/sets/:setSlug/:cardSlug/:number/*"
              element={<CardDetailPage />}
            />

            <Route path="*" element={<NotFound />} />
          </Routes>
        </main>
        <Footer />
      </BrowserRouter>
      <Toaster
        theme="dark"
        position="top-right"
        toastOptions={{
          style: {
            background: "#131518",
            color: "#F2F3F5",
            border: "1px solid #24272B",
          },
        }}
      />
    </div>
  );
}

export default function App() {
  const inner = GOOGLE_CLIENT_ID ? (
    <GoogleOAuthProvider clientId={GOOGLE_CLIENT_ID}>
      <AppRoutes />
    </GoogleOAuthProvider>
  ) : (
    // Google sign-in disabled at build time; render the app without the
    // OAuth provider so browse still works.
    <AppRoutes />
  );
  return <HelmetProvider>{inner}</HelmetProvider>;
}
